import axios from 'axios';

// Detect if accessing from mobile device and use appropriate API URL
const getApiUrl = () => {
  console.log('🔍 Detecting API URL for onboarding...');
  console.log('🔍 Current hostname:', window.location.hostname);
  console.log('🔍 Current protocol:', window.location.protocol);
  console.log('🔍 Environment API URL:', process.env.REACT_APP_API_URL);
  
  // If env variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    console.log('✅ Using env variable:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // If accessing from localhost, use localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('✅ Using localhost for development');
    return 'http://localhost:8000';
  }
  
  // Production: Use same protocol and host as frontend
  const isHttps = window.location.protocol === 'https:';
  const productionUrl = `${isHttps ? 'https' : 'http'}://${window.location.hostname}`;
  console.log('✅ Using production URL:', productionUrl);
  return productionUrl;
};

const API_BASE_URL = getApiUrl() + '/api/onboarding';
console.log('🔗 Onboarding API URL:', API_BASE_URL);

class OnboardingService {
  // Validate authentication before API calls
  validateAuth() {
    const token = localStorage.getItem('userToken');
    const userId = localStorage.getItem('userId');
    const userData = localStorage.getItem('userData');
    
    if (!token || !userId || !userData) {
      throw new Error('Authentication required. Please log in again.');
    }
    
    return { token, userId };
  }

  // Start onboarding session
  async startOnboarding() {
    try {
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/start`;
      
      console.log('=== ONBOARDING START REQUEST ===');
      console.log('URL:', url);
      console.log('Token exists:', !!token);
      
      const response = await axios.post(url, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Onboarding start response successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ ONBOARDING START ERROR:');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      
      // Handle missing backend onboarding API gracefully
      if (error.response?.status === 403 || error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        console.log('🎯 Onboarding API not available - returning mock response for development');
        return {
          success: true,
          data: {
            sessionId: `mock-session-${Date.now()}`,
            personalityId: `mock-personality-${Date.now()}`,
            firstQuestion: "What motivates you to start investing? Tell me about your financial goals and what you hope to achieve."
          }
        };
      }
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      if (error.response?.status === 409) {
        // User has already completed onboarding
        throw new Error('ALREADY_COMPLETED');
      }
      
      // Return specific error message if available
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start onboarding';
      throw new Error(errorMessage);
    }
  }

  // Start onboarding from chat with context
  async startOnboardingFromChat(conversationContext = {}) {
    try {
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/start-from-chat`;
      const payload = { conversationContext };
      
      console.log('=== ONBOARDING FROM CHAT START REQUEST ===');
      console.log('URL:', url);
      console.log('Payload:', payload);
      console.log('Token exists:', !!token);
      
      const response = await axios.post(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Chat onboarding start response successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ CHAT ONBOARDING START ERROR:');
      console.error('Error details:', error.response?.data);
      console.error('Status:', error.response?.status);
      
      // Handle missing backend onboarding API gracefully
      if (error.response?.status === 403 || error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        console.log('🎯 Chat onboarding API not available - returning mock response');
        const stockMentioned = conversationContext.stockMentioned || 'STOCK';
        return {
          success: true,
          data: {
            sessionId: `mock-chat-session-${Date.now()}`,
            personalityId: `mock-chat-personality-${Date.now()}`,
            firstQuestion: `I see you're interested in ${stockMentioned}! Before we dive into specific investment advice, let me get to know your investment style. What motivates you to start investing?`,
            fromChat: true,
            originalContext: conversationContext,
            introMessage: "Let's have a quick chat about your investment style so I can give you personalized advice!"
          }
        };
      }
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      if (error.response?.status === 409) {
        // User has already completed onboarding
        const redirectTo = error.response?.data?.redirectTo;
        const errorObj = new Error('ALREADY_COMPLETED');
        errorObj.redirectTo = redirectTo;
        throw errorObj;
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start chat onboarding';
      throw new Error(errorMessage);
    }
  }

  // Submit response and get next question
  async submitResponse(sessionId, response) {
    try {
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/response`;
      const payload = { sessionId, response };
      
      console.log('=== ONBOARDING RESPONSE SUBMISSION ===');
      console.log('URL:', url);
      console.log('Payload:', payload);
      
      const apiResponse = await axios.post(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Response submission successful:', apiResponse.data);
      return apiResponse.data;
    } catch (error) {
      console.error('❌ RESPONSE SUBMISSION ERROR:');
      console.error('Error details:', error.response?.data);
      
      // Handle missing backend onboarding API gracefully with mock responses
      if (error.response?.status === 403 || error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        console.log('🎯 Response submission API not available - returning mock response');
        
        // Simple mock logic: After 5 questions, complete the onboarding
        const questionNumber = Math.floor(Math.random() * 5) + 1;
        const isComplete = questionNumber >= 4;
        
        if (isComplete) {
          // Mock completed onboarding with a personality profile
          return {
            success: true,
            data: {
              completed: true,
              personalityProfile: {
                riskTolerance: { 
                  category: 'moderate',
                  score: 65,
                  description: 'You prefer balanced investments with moderate risk'
                },
                learningStyle: { 
                  pacePreference: 'step-by-step',
                  preferredComplexity: 'moderate',
                  needsEncouragement: false
                },
                behavioralTraits: { 
                  patience: 75,
                  analyticalDepth: 70,
                  emotionalResilience: 80
                },
                completedDate: new Date().toISOString()
              }
            }
          };
        } else {
          // Mock next question
          const questions = [
            "How do you typically handle financial decisions? Do you prefer quick choices or detailed research?",
            "What's your timeline for investment goals? Are you thinking short-term (1-2 years) or long-term (5+ years)?",
            "How do you react when your investments lose value temporarily? Do you stay calm or feel anxious?",
            "What's your experience with investing so far? Are you a complete beginner or do you have some knowledge?",
            "How important is it to you to understand exactly how your investments work before you buy them?"
          ];
          
          return {
            success: true,
            data: {
              completed: false,
              nextQuestion: questions[Math.min(questionNumber, questions.length - 1)],
              insights: {
                riskTolerance: 50 + (questionNumber * 5),
                timeHorizon: 60 + (questionNumber * 3),
                analyticalDepth: 70 + (questionNumber * 2)
              }
            }
          };
        }
      }
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit response';
      throw new Error(errorMessage);
    }
  }

  // Get user's personality profile
  async getPersonalityProfile() {
    try {
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/profile`;
      
      console.log('=== GET PERSONALITY PROFILE ===');
      console.log('URL:', url);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Personality profile retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET PERSONALITY PROFILE ERROR:');
      console.error('Error details:', error.response?.data);
      
      // If backend doesn't have onboarding endpoints yet (403/404), handle gracefully
      if (error.response?.status === 403 || error.response?.status === 404) {
        console.log('🎯 Onboarding API not available yet - treating as profile not found');
        throw new Error('PROFILE_NOT_FOUND');
      }
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      // For network errors or other issues, also treat as profile not found
      if (error.code === 'ERR_NETWORK' || !error.response) {
        console.log('🎯 Network error accessing onboarding API - treating as profile not found');
        throw new Error('PROFILE_NOT_FOUND');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get personality profile';
      throw new Error(errorMessage);
    }
  }

  // Reset personality profile (for testing or preference changes)
  async resetPersonalityProfile() {
    try {
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/reset`;
      
      console.log('=== RESET PERSONALITY PROFILE ===');
      console.log('URL:', url);
      
      const response = await axios.delete(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Personality profile reset successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ RESET PERSONALITY PROFILE ERROR:');
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reset personality profile';
      throw new Error(errorMessage);
    }
  }

  // Update adaptive profile based on trading behavior
  async updateAdaptiveProfile(behaviorData) {
    try {
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/adaptive`;
      
      console.log('=== UPDATE ADAPTIVE PROFILE ===');
      console.log('URL:', url);
      console.log('Behavior data:', behaviorData);
      
      const response = await axios.put(url, behaviorData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Adaptive profile update successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ UPDATE ADAPTIVE PROFILE ERROR:');
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update adaptive profile';
      throw new Error(errorMessage);
    }
  }

  // Check if user has completed onboarding (utility method)
  async hasCompletedOnboarding() {
    try {
      await this.getPersonalityProfile();
      return true;
    } catch (error) {
      if (error.message === 'PROFILE_NOT_FOUND') {
        return false;
      }
      // Re-throw other errors
      throw error;
    }
  }

  // Get cached personality profile from localStorage (for offline access)
  getCachedPersonalityProfile() {
    try {
      const cachedProfile = localStorage.getItem('personalityProfile');
      if (cachedProfile) {
        const parsed = JSON.parse(cachedProfile);
        const cacheTime = localStorage.getItem('personalityProfileCacheTime');
        
        // Check if cache is still valid (extended to 7 days for better UX)
        if (cacheTime && Date.now() - parseInt(cacheTime) < 7 * 24 * 60 * 60 * 1000) {
          console.log('🎯 Using cached personality profile (valid)');
          return parsed;
        } else if (cacheTime) {
          console.log('🎯 Cached personality profile expired, but checking completion flag');
          // Even if cache expired, check if user had completed before
          const completionFlag = this.getCompletionFlag();
          if (completionFlag) {
            console.log('🎯 Using expired cache since completion flag indicates user completed before');
            return parsed; // Allow expired cache if user definitely completed
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error reading cached personality profile:', error);
      return null;
    }
  }

  // Cache personality profile for offline access
  cachePersonalityProfile(profile) {
    try {
      localStorage.setItem('personalityProfile', JSON.stringify(profile));
      localStorage.setItem('personalityProfileCacheTime', Date.now().toString());
      // Set completion flag as backup
      this.setCompletionFlag(true);
      console.log('🎯 Personality profile cached with completion flag');
    } catch (error) {
      console.error('Error caching personality profile:', error);
    }
  }

  // Clear cached personality profile
  clearCachedPersonalityProfile() {
    localStorage.removeItem('personalityProfile');
    localStorage.removeItem('personalityProfileCacheTime');
    this.setCompletionFlag(false);
    console.log('🎯 Personality profile cache and completion flag cleared');
  }

  // Backup completion flag for additional persistence
  setCompletionFlag(completed) {
    try {
      localStorage.setItem('onboardingCompleted', completed.toString());
      localStorage.setItem('onboardingCompletedTime', Date.now().toString());
    } catch (error) {
      console.error('Error setting completion flag:', error);
    }
  }

  // Get completion flag
  getCompletionFlag() {
    try {
      const flag = localStorage.getItem('onboardingCompleted');
      return flag === 'true';
    } catch (error) {
      console.error('Error getting completion flag:', error);
      return false;
    }
  }
}

export default new OnboardingService();